import { useEffect, useRef } from 'react';

/**
 * BroadcastChannel bridge between the staff POS screen and the customer-facing
 * screen (/customer-display) on the same machine / same origin.
 *
 * The staff screen publishes cart updates; the customer route listens. Zero
 * backend dependency — works today on the dual-screen till where both screens
 * are windows of the same Chromium profile.
 */

export const CUSTOMER_DISPLAY_CHANNEL = 'sog-edge-customer-display';

export interface CustomerDisplayLine {
  name: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export type CustomerDisplayMessage =
  | { type: 'cart'; lines: CustomerDisplayLine[]; total: number }
  | { type: 'settled' };

/** Minimal structural view of a cart item (mirrors CartContext's CartItem). */
interface BroadcastableCartItem {
  menuItem: { id: number; name?: string };
  quantity: number;
  price: number;
  portionName?: string;
}

/**
 * Wired into CartProvider with a single hook call. Publishes {type:'cart'}
 * on every cart change, and {type:'settled'} when a non-empty cart empties
 * (order placed / paid), so the customer screen can show "Thank you".
 *
 * Inert unless VITE_EDGE_MODE === 'true' (or BroadcastChannel is unavailable).
 */
export function useCustomerDisplayBroadcast(
  items: BroadcastableCartItem[],
  getTotal: () => number,
): void {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const prevCountRef = useRef(0);

  const enabled =
    import.meta.env.VITE_EDGE_MODE === 'true' && typeof BroadcastChannel !== 'undefined';

  useEffect(() => {
    if (!enabled) return;
    const channel = new BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL);
    channelRef.current = channel;
    return () => {
      channelRef.current = null;
      channel.close();
    };
  }, [enabled]);

  useEffect(() => {
    const channel = channelRef.current;
    if (!channel) return;

    const hadItems = prevCountRef.current > 0;
    prevCountRef.current = items.length;

    try {
      if (items.length === 0 && hadItems) {
        // Cart just emptied — treat as order settled.
        const message: CustomerDisplayMessage = { type: 'settled' };
        channel.postMessage(message);
        return;
      }

      const lines: CustomerDisplayLine[] = items.map(item => ({
        name:
          (item.menuItem.name ?? `Item ${item.menuItem.id}`) +
          (item.portionName ? ` (${item.portionName})` : ''),
        quantity: item.quantity,
        unitPrice: item.price,
        lineTotal: item.price * item.quantity,
      }));
      const message: CustomerDisplayMessage = { type: 'cart', lines, total: getTotal() };
      channel.postMessage(message);
    } catch {
      // Broadcasting is best-effort; never break the POS over display sync.
    }
    // getTotal is stable enough (memoised on items in CartContext); keying on
    // items keeps this to one publish per cart change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, enabled]);
}

export default useCustomerDisplayBroadcast;
